import React from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  NavLink,
  Navigate,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Button,
  Container,
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  IconButton,
  useMediaQuery,
  CircularProgress,
  Collapse,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { fetchApi } from './components/fetchApi';
import TwoFactorPage from './components/TwoFactorPage';

import Dashboard from './components/Dashboard';
import ArtikelFormular from './components/ArtikelFormular';

import LagerstandProArtikelTabelle from './components/LagerstandProArtikelTabelle';
import ArtikelUnterMindestbestandTabelle from './components/ArtikelUnterMindestbestandTabelle';
import ArtikelOhneBestandTabelle from './components/ArtikelOhneBestandTabelle';
import ArtikelProLagerplatzTabelle from './components/ArtikelProLagerplatzTabelle';
import BewegungshistorieKompaktTabelle from './components/BewegungshistorieKompaktTabelle';
import LagerwertGesamtTabelle from './components/LagerwertGesamtTabelle';

import LieferscheinFormular from './components/LieferscheinFormular';

import KategorieFormular from './components/KategorieFormular';
import ChildFormular from './components/ChildFormular';
import ParentChildListe from './components/ParentChildListe';
import ParentBearbeiten from './components/ParentBearbeiten';
import ChildBearbeiten from './components/ChildBearbeiten';
import LieferantFormular from './components/LieferantFormular';
import LagerorteVerwaltung from './components/LagerorteVerwaltung';

import LieferscheinListe from './components/LieferscheinListe';
import LieferscheinDetails from './components/LieferscheinDetails';
import LieferscheinBearbeiten from './components/LieferscheinBearbeiten';
import LieferscheinPacken from './components/LieferscheinPacken';
import LieferscheinEinlagern from './components/LieferscheinEinlagern';
import LieferscheinPackzettel from './components/LieferscheinPackzettel';
import LeereBoxenListe from './components/LeereBoxenListe';

import ImportSeite from './components/ImportSeite';

import LoginPage from './components/LoginPage';

import './print.css';

const drawerWidth = 300;

// Breakpoint: ab "md" = permanent, darunter = Burger/Overlay
const DESKTOP_UP = 'md' as const;

function AppInner() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up(DESKTOP_UP));
  const location = useLocation();
  const navigate = useNavigate();

  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [bestandOpen, setBestandOpen] = React.useState(false);
  const [berichteOpen, setBerichteOpen] = React.useState(false);

  const [authChecked, setAuthChecked] = React.useState(false);
  const [isAuthed, setIsAuthed] = React.useState(false);

  // 2FA Status (vom Backend)
  const [twoFactorRequired, setTwoFactorRequired] = React.useState(false);
  const [twoFactorVerified, setTwoFactorVerified] = React.useState(false);

  const isLoginRoute = location.pathname === '/login';
  const isTwoFaRoute = location.pathname === '/2fa';

  const handleDrawerToggle = () => {
    setMobileOpen((prev) => !prev);
  };

  const handleCloseMobileDrawer = () => {
    setMobileOpen(false);
  };

// /api/auth/me prüfen, Session + 2FA Status setzen
async function checkMe() {
  try {
    const res = await fetchApi('/api/auth/me', { cache: 'no-store' });

    const data = await res.json().catch(() => null);
    const userId = data?.user?.id;

    if (!userId) {
      setIsAuthed(false);
      setTwoFactorRequired(false);
      setTwoFactorVerified(false);
      return null;
    }

    setIsAuthed(true);
    setTwoFactorRequired(data?.twoFactorRequired === true);
    setTwoFactorVerified(data?.twoFactorVerified === true);

    return data;
  } catch {
    setIsAuthed(false);
    setTwoFactorRequired(false);
    setTwoFactorVerified(false);
    return null;
  }
}

  React.useEffect(() => {
    (async () => {
      await checkMe();
      setAuthChecked(true);
    })();
  }, []);

  async function handleLogout() {
    try {
      await fetchApi('/api/auth/logout', { method: 'POST' });
    } catch {
      // egal, wir gehen trotzdem auf login
    } finally {
      setIsAuthed(false);
      setTwoFactorRequired(false);
      setTwoFactorVerified(false);
      navigate('/login', { replace: true });
    }
  }

  // Während /me noch läuft: simples Loading (verhindert "Flash" von App)
  if (!authChecked) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  // ===== ROUTE: /login =====
  if (isLoginRoute) {
    // Wenn bereits eingeloggt, aber 2FA fehlt -> direkt /2fa
    if (isAuthed && twoFactorRequired && !twoFactorVerified) {
      return <Navigate to="/2fa" replace />;
    }

    // Wenn eingeloggt und 2FA ok -> App
    if (isAuthed) return <Navigate to="/" replace />;

    return (
      <LoginPage
        onLoginSuccess={async () => {
          const me = await checkMe();

          if (me?.twoFactorRequired === true && me?.twoFactorVerified === false) {
            navigate('/2fa', { replace: true });
            return;
          }

          navigate('/', { replace: true });
        }}
      />
    );
  }

  // ===== ROUTE: /2fa =====
  if (isTwoFaRoute) {
    // Nicht eingeloggt -> login
    if (!isAuthed) return <Navigate to="/login" replace />;

    // Schon verifiziert -> App
    if (twoFactorRequired && twoFactorVerified) return <Navigate to="/" replace />;
    // Falls 2FA gar nicht erforderlich ist -> App
    if (!twoFactorRequired) return <Navigate to="/" replace />;

    return (
      <TwoFactorPage
        onVerified={async () => {
          const me = await checkMe();
          if (me?.twoFactorVerified === true) {
            navigate('/', { replace: true });
          }
        }}
      />
    );
  }

  // ===== ALLE ANDEREN ROUTEN: geschützt =====
  if (!isAuthed) {
    return <Navigate to="/login" replace />;
  }

  // 2FA Gate: wenn erforderlich aber nicht verifiziert -> /2fa
  if (twoFactorRequired && !twoFactorVerified) {
    return <Navigate to="/2fa" replace />;
  }

  // Gemeinsames Styling für Sidebar-Navigation (hover + active)
  const navItemSx = {
    borderRadius: 1,
    mb: 0.2,

    // Default Textfarbe in Sidebar
    '& .MuiListItemText-primary': {
      color: '#fff',
    },

    // Hover (nur visuell, Farbe kannst du später ändern)
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.08)',
    },

    // Active State (NavLink setzt .active)
    '&.active': {
      backgroundColor: '#FF7E62',
    },
    '&.active .MuiListItemText-primary': {
      color: '#fff',
      fontWeight: 600,
    },
  } as const;

  const drawerContent = (
    <Box sx={{ height: '100%' }}>
      {/* LOGO BEREICH */}
      <Box sx={{ px: 2, py: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box component="img" src="/logo.svg" alt="Logo" sx={{ height: 25, width: 'auto' }} />
        <Box component="span" sx={{ color: '#fff', fontWeight: 600 }}>
          Warenwirtschaft 2.0
        </Box>
      </Box>

      <Divider />

      {/* NAVIGATION */}
      <List sx={{ px: 1, py: 0.5 }}>
        <ListItemButton
          component={NavLink}
          to="/"
          end
          onClick={!isDesktop ? handleCloseMobileDrawer : undefined}
          sx={navItemSx}
        >
          <ListItemText primary="Dashboard" />
        </ListItemButton>

        <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.12)' }} />

        <ListItemButton
          component={NavLink}
          to="/artikel"
          end
          onClick={!isDesktop ? handleCloseMobileDrawer : undefined}
          sx={navItemSx}
        >
          <ListItemText primary="Parent-Artikel anlegen" />
        </ListItemButton>

        <ListItemButton
          component={NavLink}
          to="/child"
          onClick={!isDesktop ? handleCloseMobileDrawer : undefined}
          sx={navItemSx}
        >
          <ListItemText primary="Child-Artikel anlegen" />
        </ListItemButton>

        <ListItemButton
          component={NavLink}
          to="/kategorien"
          onClick={!isDesktop ? handleCloseMobileDrawer : undefined}
          sx={navItemSx}
        >
          <ListItemText primary="Kategorien" />
        </ListItemButton>

        <ListItemButton
          component={NavLink}
          to="/lieferanten"
          onClick={!isDesktop ? handleCloseMobileDrawer : undefined}
          sx={navItemSx}
        >
          <ListItemText primary="Lieferanten" />
        </ListItemButton>

        <ListItemButton
          component={NavLink}
          to="/lagerorte"
          onClick={!isDesktop ? handleCloseMobileDrawer : undefined}
          sx={navItemSx}
        >
          <ListItemText primary="Lagerorte anlegen" />
        </ListItemButton>

        <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.12)' }} />

        <ListItemButton
          component={NavLink}
          to="/artikel-liste"
          onClick={!isDesktop ? handleCloseMobileDrawer : undefined}
          sx={navItemSx}
        >
          <ListItemText primary="Artikel-Liste" />
        </ListItemButton>

        <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.12)' }} />

        <ListItemButton
          component={NavLink}
          to="/lieferschein"
          onClick={!isDesktop ? handleCloseMobileDrawer : undefined}
          sx={navItemSx}
        >
          <ListItemText primary="Lieferschein erstellen" />
        </ListItemButton>

        <ListItemButton
          component={NavLink}
          to="/lieferschein-liste"
          onClick={!isDesktop ? handleCloseMobileDrawer : undefined}
          sx={navItemSx}
        >
          <ListItemText primary="Lieferscheine Übersicht" />
        </ListItemButton>

        <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.12)' }} />

        {/* Bestand (Collapse) */}
        <ListItemButton
          onClick={() => setBestandOpen(!bestandOpen)}
          sx={navItemSx}
        >
          <ListItemText primary="Bestand" />
          {bestandOpen ? <ExpandLess sx={{ color: '#fff' }} /> : <ExpandMore sx={{ color: '#fff' }} />}
        </ListItemButton>
        <Collapse in={bestandOpen} timeout="auto" unmountOnExit>
          <List disablePadding sx={{ pl: 1 }}>
            <ListItemButton
              component={NavLink}
              to="/lagerstand"
              onClick={!isDesktop ? handleCloseMobileDrawer : undefined}
              sx={navItemSx}
            >
              <ListItemText primary="Lagerstand pro Artikel" />
            </ListItemButton>

            <ListItemButton
              component={NavLink}
              to="/leere-boxen"
              onClick={!isDesktop ? handleCloseMobileDrawer : undefined}
              sx={navItemSx}
            >
              <ListItemText primary="Leere Boxen" />
            </ListItemButton>

            <ListItemButton
              component={NavLink}
              to="/artikel-ohne-bestand"
              onClick={!isDesktop ? handleCloseMobileDrawer : undefined}
              sx={navItemSx}
            >
              <ListItemText primary="Artikel ohne Bestand" />
            </ListItemButton>

          </List>
        </Collapse>

        <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.12)' }} />

        {/* Berichte (Collapse) */}
        <ListItemButton
          onClick={() => setBerichteOpen(!berichteOpen)}
          sx={navItemSx}
        >
          <ListItemText primary="Berichte" />
          {berichteOpen ? <ExpandLess sx={{ color: '#fff' }} /> : <ExpandMore sx={{ color: '#fff' }} />}
        </ListItemButton>
        <Collapse in={berichteOpen} timeout="auto" unmountOnExit>
          <List disablePadding sx={{ pl: 1 }}>
            <ListItemButton
              component={NavLink}
              to="/mindestbestand"
              onClick={!isDesktop ? handleCloseMobileDrawer : undefined}
              sx={navItemSx}
            >
              <ListItemText primary="Artikel unter Mindestbestand" />
            </ListItemButton>

            <ListItemButton
              component={NavLink}
              to="/artikel-pro-lagerplatz"
              onClick={!isDesktop ? handleCloseMobileDrawer : undefined}
              sx={navItemSx}
            >
              <ListItemText primary="Artikel pro Lagerplatz" />
            </ListItemButton>

            <ListItemButton
              component={NavLink}
              to="/bewegungshistorie"
              onClick={!isDesktop ? handleCloseMobileDrawer : undefined}
              sx={navItemSx}
            >
              <ListItemText primary="Bewegungshistorie" />
            </ListItemButton>

            <ListItemButton
              component={NavLink}
              to="/lagerwert"
              onClick={!isDesktop ? handleCloseMobileDrawer : undefined}
              sx={navItemSx}
            >
              <ListItemText primary="Lagerwert gesamt" />
            </ListItemButton>
          </List>
        </Collapse>

        <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.12)' }} />

        {/* Import */}
        <ListItemButton
          component={NavLink}
          to="/import"
          onClick={!isDesktop ? handleCloseMobileDrawer : undefined}
          sx={navItemSx}
        >
          <ListItemText primary="Import" />
        </ListItemButton>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* MOBILE/TABLET Drawer (Burger) */}
      {!isDesktop && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }} // bessere Performance auf Mobile
          sx={{
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              bgcolor: '#121621',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* DESKTOP Drawer (Permanent) */}
      {isDesktop && (
        <Drawer
          variant="permanent"
          open
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              bgcolor: '#121621',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* RECHTS: HEADER + CONTENT */}
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          width: isDesktop ? `calc(100% - ${drawerWidth}px)` : '100%',
        }}
      >
        {/* HEADER */}
        <AppBar
          position="sticky"
          sx={{
            bgcolor: '#ffffff',
            boxShadow: 'none',
            borderBottom: '1px solid rgba(0,0,0,0.08)',
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
        >
          <Toolbar sx={{ justifyContent: 'space-between' }}>
            {/* Burger-Button (nur Mobile) */}
            {!isDesktop ? (
              <IconButton edge="start" onClick={handleDrawerToggle} aria-label="open navigation">
                <MenuIcon />
              </IconButton>
            ) : (
              <Box />
            )}

            {/* RECHTS: Logout */}
            <Button
              color="primary"
              variant="text"
              onClick={handleLogout}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              Logout
            </Button>
          </Toolbar>
        </AppBar>

        {/* CONTENT */}
        <Box sx={{ flexGrow: 1 }}>
          <Container sx={{ py: 2, pt: 3 }}>
            <Routes>
              <Route path="/" element={<Dashboard />} />

              <Route path="/artikel" element={<ArtikelFormular />} />
              <Route path="/child" element={<ChildFormular />} />
              <Route path="/artikel-liste" element={<ParentChildListe />} />
              <Route path="/artikel/parent/bearbeiten/:id" element={<ParentBearbeiten />} />
              <Route path="/artikel/child/bearbeiten/:id" element={<ChildBearbeiten />} />

              <Route path="/kategorien" element={<KategorieFormular />} />
              <Route path="/lieferanten" element={<LieferantFormular />} />
              <Route path="/lagerorte" element={<LagerorteVerwaltung />} />

              {/* <Route path="/bewegung" element={<BewegungFormular />} /> */}

              <Route path="/lieferschein-liste" element={<LieferscheinListe />} />

              <Route path="/lieferschein/einlagern/:id" element={<LieferscheinEinlagern />} />
              <Route path="/lieferschein/packen/:id" element={<LieferscheinPacken />} />
              <Route path="/lieferschein/edit/:id" element={<LieferscheinBearbeiten />} />
              <Route path="/lieferschein/packzettel/:id" element={<LieferscheinPackzettel />} />

              <Route path="/lieferschein/:id" element={<LieferscheinDetails />} />
              <Route path="/lieferschein" element={<LieferscheinFormular />} />

              <Route path="/leere-boxen" element={<LeereBoxenListe />} />
              <Route path="/import" element={<ImportSeite />} />

              <Route path="/lagerstand" element={<LagerstandProArtikelTabelle />} />

              <Route path="/artikel-ohne-bestand" element={<ArtikelOhneBestandTabelle />} />

              <Route path="/mindestbestand" element={<ArtikelUnterMindestbestandTabelle />} />
              <Route path="/artikel-pro-lagerplatz" element={<ArtikelProLagerplatzTabelle />} />
              <Route path="/bewegungshistorie" element={<BewegungshistorieKompaktTabelle />} />
              <Route path="/lagerwert" element={<LagerwertGesamtTabelle />} />

              {/* Fallback */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Container>
        </Box>
      </Box>
    </Box>
  );
}

function App() {
  return (
    <Router>
      <AppInner />
    </Router>
  );
}

export default App;
